<template>
  <ClientOnly>
    <div class="tooltips">
      <transition v-for="tooltip in tooltips" :key="tooltip.elementId" appear name="fade">
        <div :class="tooltip.classList" :style="tooltip.style">
          <div class="tooltips__tooltip-inner">
            <BravadoText v-if="tooltip.title" variant="body-semibold" class="tooltips__tooltip-title">
              {{ tooltip.title }}
            </BravadoText>
            <BravadoText variant="body" class="tooltips__tooltip-text" v-html="tooltip.text" />
          </div>
        </div>
      </transition>
    </div>
  </ClientOnly>
</template>

<script lang="ts">
import Vue from 'vue';
import { events, getTooltipPosition } from './utils';
import { BIND_DIRECTIVE_EVENT, UNBIND_DIRECTIVE_EVENT, UPDATE_TOOLTIP_EVENT } from './constants';
import type { Element, BindHookPayload, TooltipConfig, TooltipWithConfigFields } from './types';
import { TooltipDirection, TooltipPosition } from './types';
import type { Tooltip } from '@/types/common';
import { uid } from '@/components/utils/core/uid';

interface ComponentData {
  tooltips: TooltipWithConfigFields[];
  elements: Element[];
}

export default Vue.extend({
  name: 'Tooltip',
  data(): ComponentData {
    return {
      tooltips: [],
      elements: [],
    };
  },
  beforeMount(): void {
    events.$on(BIND_DIRECTIVE_EVENT, this.addElement);
    events.$on(UNBIND_DIRECTIVE_EVENT, this.removeElement);
    events.$on(UPDATE_TOOLTIP_EVENT, this.updateTooltip);
  },
  beforeDestroy(): void {
    events.$off(BIND_DIRECTIVE_EVENT, this.addElement);
    events.$off(UNBIND_DIRECTIVE_EVENT, this.removeElement);
    events.$off(UPDATE_TOOLTIP_EVENT, this.updateTooltip);
  },
  methods: {
    addElement({ el, binding }: BindHookPayload): void {
      if (!binding.value) return;

      const { title, text, variant } = this.getTextValues(binding.value);

      const elementId = uid();

      const { bottom, left, right, ...rest } = binding.modifiers;

      if (Object.keys(rest).length) {
        throw new Error('Invialid modifiers provided');
      }

      let direction = TooltipDirection.Top;
      let position = TooltipPosition.Center;

      if (bottom) {
        direction = TooltipDirection.Bottom;
      }

      if (left) {
        position = TooltipPosition.Left;
      }

      if (right) {
        position = TooltipPosition.Right;
      }

      el.dataset.elementId = elementId;
      this.addHandlers(el);

      if (!el.style.position) {
        el.style.position = 'relative';
      }

      this.elements.push({
        id: elementId,
        el,
        tooltipConfig: {
          title,
          text,
          position,
          direction,
          variant,
        },
      });
    },
    addHandlers(el: HTMLElement): void {
      el.addEventListener('mouseenter', this.handleElementEnter);
      el.addEventListener('mouseleave', this.handleElementLeave);
    },
    removeHandlers(el: HTMLElement) {
      el.removeEventListener('mouseenter', this.handleElementEnter);
      el.removeEventListener('mouseleave', this.handleElementLeave);
    },
    handleElementEnter({ target }: Event) {
      const elementId = (target as HTMLElement).dataset.elementId;
      const element = this.elements.find(element => element.id === elementId);

      if (!element) return;

      const { tooltipConfig } = element;

      const tooltip = {
        style: this.getTooltipPosition(target as HTMLElement, tooltipConfig),
        elementId,
        classList: this.getClasses(tooltipConfig),
        ...tooltipConfig,
      };

      this.tooltips.push(tooltip);
    },

    handleElementLeave({ target }: Event): void {
      const elementId = (target as HTMLElement).dataset.elementId;
      this.tooltips = this.tooltips.filter(tooltip => tooltip.elementId !== elementId);
    },
    removeElement(el: HTMLElement): void {
      this.removeHandlers(el);
      const elementId = el.dataset.elementId;

      this.elements = this.elements.filter(element => element.id !== elementId);
    },
    updateTooltip({ el, binding }: BindHookPayload): void {
      const elementId = el.dataset.elementId;

      if (!elementId) return;
      const element = this.elements.find(element => element.id === elementId);

      if (!element) return;

      if (!binding.value) {
        this.removeElement(el);
        return;
      }

      const { title, text } = this.getTextValues(binding.value);
      const { title: currentTitle, text: currentText } = element.tooltipConfig;

      if (title === currentTitle && text === currentText) return;

      element.tooltipConfig.title = title;
      element.tooltipConfig.text = text;
    },
    getTextValues(value: Tooltip | string) {
      if (typeof value === 'string') {
        return { text: value };
      } else {
        const { title, text, variant } = value;
        return { title, text, variant };
      }
    },
    getClasses(tooltip: TooltipConfig) {
      const BASE_CLASS = 'tooltips__tooltip';
      return [
        BASE_CLASS,
        `${BASE_CLASS}--${tooltip.variant}`,
        `${BASE_CLASS}--${tooltip.direction}`,
        `${BASE_CLASS}--${tooltip.position}`,
      ];
    },
    getTooltipPosition,
  },
});
</script>

<style scoped lang="scss">
.tooltips {
  $this: &;

  &__tooltip {
    background: var(--theme-color-card);
    color: var(--theme-color-text);
    padding: 8px 12px;
    position: fixed;
    max-width: 300px;
    pointer-events: none;
    z-index: 1001;

    &-inner {
      white-space: nowrap;
      overflow: hidden;
    }

    &--primary {
      border-radius: 12px;
      background: var(--theme-color-text);
      width: 264px;
      padding: 16px;

      &::before {
        content: '';
        position: absolute;
        width: 12px;
        height: 12px;
        background: var(--theme-color-text);
        transform: rotate(45deg);
        border-radius: 2px;
      }

      #{$this}__tooltip-inner {
        white-space: normal;
      }

      #{$this}__tooltip-title {
        color: var(--theme-color-card);
        margin-bottom: 5px;
      }

      #{$this}__tooltip-text {
        @include subhead;

        color: var(--theme-color-card);
        opacity: 0.6;
      }
    }

    &--bottom::before {
      top: -6px;
    }

    &--top::before {
      bottom: -6px;
    }

    &--left::before {
      right: 18px;
    }

    &--right::before {
      left: 18px;
    }

    &--center::before {
      left: calc(50% - 6px);
    }
  }

  .fade-enter-active {
    animation: fade 0.2s;
  }

  .fade-leave-active {
    animation: fade 0.2s reverse;
  }

  @keyframes fade {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }
}
</style>
